// file: loadBaseLayers.js
import { Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { all as allStrategy, bbox as bboxStrategy, tile as tileStrategy } from 'ol/loadingstrategy';
import { createXYZ } from 'ol/tilegrid';

// Adreslaag (BE): alleen de velden die de kaart toont of gebruikt.
// Nieuwe GeoServer-set sinds 15 okt 2026; geometrie heet 'lnglat'.
const BE_ADDRESS_TYPENAME = 'geolinq:be_full_address';
// Het veld '4pp' (postcode) kan niet mee: WFS weigert een veldnaam die met
// een cijfer begint. Postcode staat op de be_4pp-laag.
const BE_ADDRESS_PROPERTIES = [
  'lnglat', 'straatnaam', 'huisnunmmer', 'huisnummertoevoeging',
  'plaats', 'gemeente', 'cd_sector'
];

// De backend-config (base-layers/raw) verwijst nog naar de oude tabellen.
// Zelfde vervangtabel als upstream gis-frontend 1.0.6.30 (commit 62e1811).
// Rechten en titels blijven op de oorspronkelijke laag gebaseerd.
const TYPENAME_REPLACEMENTS = {
  'geolinq:u101093viewvbodienstversietype': 'geolinq:nl_full_address',
  'geolinq:u101234be_adres': BE_ADDRESS_TYPENAME,
  'geolinq:u1014134viewgroupadrestype1': 'geolinq:be_4pp',
  'geolinq:u101393viewgroupadrestype1': 'geolinq:be_country',
  'geolinq:u101403viewgroupadrestype1': 'geolinq:be_regions',
  'geolinq:u101405viewgroupadrestype1': 'geolinq:be_provinces',
  'geolinq:u101409viewgroupadrestype1': 'geolinq:be_arrondissements',
  'geolinq:u101411viewgroupadrestype1': 'geolinq:be_municipalities',
  'geolinq:u101413viewgroupadrestype1': 'geolinq:be_statistical_sectors',
  'geolinq:be_deelgemeenten': 'geolinq:be_statistical_sectors',
  'geolinq:u101389viewgroupvbotype1': 'geolinq:nl_landgebied',
  'geolinq:u101379viewgroupvbotype1': 'geolinq:nl_provinciegebied',
  'geolinq:u101377viewgroupvbotype1': 'geolinq:nl_wijken',
  'geolinq:u101375viewgroupvbotype1': 'geolinq:nl_buurten',
  'geolinq:u101365viewgroupvbotype1': 'geolinq:nl_pc6',
  'geolinq:nl_4pp': 'geolinq:nl_pc6',
  'geolinq:u101373viewgroupvbotype1': 'geolinq:nl_6pp'
};

const OVERVIEW_TYPENAMES = new Set([
  'geolinq:be_country', 'geolinq:be_regions', 'geolinq:be_provinces',
  'geolinq:be_arrondissements', 'geolinq:be_municipalities',
  'geolinq:nl_landgebied', 'geolinq:nl_provinciegebied'
]);

/**
 * GeoJSON-formaat dat extra geometriekolommen uit de properties haalt. De
 * nieuwe Sevenn-lagen (be_4pp, ...) hebben naast de echte geometrie nog
 * kolommen 'geometry' en 'lnglat'; een property 'geometry' overschrijft in
 * OpenLayers de feature-geometrie met een kaal object en dan tekent de laag
 * niets meer.
 */
class SanitizedGeoJSON extends GeoJSON {
  readFeatures(source, options) {
    const data = typeof source === 'string' ? JSON.parse(source) : source;
    if (data && Array.isArray(data.features)) {
      for (const f of data.features) {
        const props = f?.properties;
        if (!props) continue;
        for (const key of Object.keys(props)) {
          const v = props[key];
          if (v && typeof v === 'object' && typeof v.type === 'string' && 'coordinates' in v) delete props[key];
        }
      }
    }
    return super.readFeatures(data, options);
  }
}

function withReplacedTypeName(layerInfo) {
  const key = String(layerInfo.typeName || '').trim().toLowerCase();
  const replacement = TYPENAME_REPLACEMENTS[key];
  return replacement ? { ...layerInfo, typeName: replacement } : layerInfo;
}
// Adrespunten pas vanaf ongeveer zoom 16; daarboven zijn het er te veel om te
// laden en te tekenen, wat de kaart liet bevriezen.
const ADDRESS_MAX_RESOLUTION = 2.0;
const ADDRESS_MIN_ZOOM = 16;
const ADDRESS_TILE_GRID = createXYZ({ tileSize: 512 });
import { GEOSERVER_URL, API_URL } from './config.js';
import * as MapUtils from './mapUtils.js';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import * as olExtent from 'ol/extent';
import { getSelectedCountryCode, getSelectedLanguageCode } from './country-language.js';
import { secureFetch } from "./securityUtil.js";
import { updateBaseLegendMessage } from './mapUtils.js';
import { getMap } from './mapInstance.js';
import HeatmapLayer from 'ol/layer/Heatmap';
import { getUserRights, hasRight } from './rightsUtil.js';
let isHeatmapMode = false;

const BASE_LAYER_RIGHT_ALIASES = {
  addresses: ['address', 'addresses', 'adres', 'adressen', 'adresse', 'adresses'],
  areas: ['area'],
  councils: ['council'],
  countries: ['country'],
  neighbourhoods: ['neighbourhood', 'neighborhood', 'neighborhoods'],
  pc4: ['pc4', 'postcode4', 'postalcode4', 'codepostal4'],
  pc5: ['pc5', 'postcode5', 'postalcode5', 'codepostal5'],
  pc6: ['pc6', 'postcode6', 'postalcode6', 'codepostal6'],
  regions: ['region']
};


export function toggleHeatmapMode() {
  isHeatmapMode = !isHeatmapMode;

  loadDynamicBaseLayers(getMap());
}

function extractHeatmapColors(styleJson) {
  try {
    if (!styleJson) return ['#0000FF', '#FF0000']; // fallback

    const parsed = JSON.parse(styleJson);

    // Try fill color first
    if (parsed.fill && parsed.fill.color) {
      return generateGradient(parsed.fill.color);
    }

    // Try stroke color
    if (parsed.stroke && parsed.stroke.color) {
      return generateGradient(parsed.stroke.color);
    }

    // Try point color
    if (parsed.image && parsed.image.fill?.color) {
      return generateGradient(parsed.image.fill.color);
    }

    return ['#0000FF', '#FF0000'];

  } catch (e) {
    console.warn("Failed to extract heatmap color", e);
    return ['#0000FF', '#FF0000'];
  }
}

function extractRGB(color) {
  // handle rgba or rgb
  if (color.startsWith('rgb')) {
    const nums = color.match(/\d+/g).map(Number);
    return { r: nums[0], g: nums[1], b: nums[2] };
  }

  // handle hex (#RRGGBB)
  if (color.startsWith('#')) {
    const bigint = parseInt(color.slice(1), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }

  // fallback (blue)
  return { r: 0, g: 0, b: 255 };
}

function generateGradient(baseColor) {
  const rgb = extractRGB(baseColor);

  return [
    'rgba(0,0,0,0)',                        // transparent
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.3)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.6)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.9)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},1)`
  ];
}

// expose to HTML
window.toggleHeatmapMode = toggleHeatmapMode;


export function parseStyleFromJSON(styleJson) {
  try {
    if (!styleJson) return getDefaultStyle();

    const parsed = JSON.parse(styleJson);

    // Polygon-style
    if (parsed.fill || parsed.stroke) {
      return new Style({
        fill: parsed.fill ? new Fill({ color: parsed.fill.color }) : undefined,
        stroke: parsed.stroke ? new Stroke({ color: parsed.stroke.color, width: parsed.stroke.width }) : undefined
      });
    }

    // Point-style (circle)
    if (parsed.image) {
      return new Style({
        image: new CircleStyle({
          radius: parsed.image.radius || 5,
          fill: parsed.image.fill ? new Fill({ color: parsed.image.fill.color }) : undefined,
          stroke: parsed.image.stroke
            ? new Stroke({ color: parsed.image.stroke.color, width: parsed.image.stroke.width })
            : undefined
        })
      });
    }

    return getDefaultStyle();
  } catch (err) {
    console.warn('Failed to parse style JSON:', err);
    return getDefaultStyle();
  }
}

function isClosedPolygonGeometry(feature) {
  const type = feature?.getGeometry?.()?.getType?.();
  return type === 'Polygon' || type === 'MultiPolygon';
}

function createUniformPolygonStyle(backendStyle) {
  const fillColor = backendStyle?.getFill?.()?.getColor?.();
  const stroke = backendStyle?.getStroke?.();

  return new Style({
    fill: fillColor ? new Fill({ color: fillColor }) : undefined,
    stroke: stroke
      ? new Stroke({
          color: stroke.getColor(),
          width: stroke.getWidth(),
          lineCap: stroke.getLineCap(),
          lineJoin: stroke.getLineJoin(),
          lineDash: stroke.getLineDash(),
          lineDashOffset: stroke.getLineDashOffset(),
          miterLimit: stroke.getMiterLimit()
        })
      : undefined
  });
}

function createBaseLayerStyleFunction(styleJson) {
  const backendStyle = parseStyleFromJSON(styleJson);
  const polygonStyle = createUniformPolygonStyle(backendStyle);
  const pointStyle = backendStyle?.getImage?.()
    ? backendStyle
    : getDefaultStyle();

  return feature => {
    if (!feature) {
      return pointStyle;
    }

    if (isClosedPolygonGeometry(feature)) {
      return polygonStyle;
    }

    return pointStyle;
  };
}

// Adrespunten: effen rode bolletjes, zonder rand. De backend-stijl heeft een
// gele rand van 4 px; die kost bij duizenden punten merkbaar tekentijd en
// maakt de kaart drukker. Daarom hier vast, los van de backend-config.
const addressPointStyle = new Style({
  image: new CircleStyle({
    radius: 4,
    fill: new Fill({ color: 'rgba(220, 30, 30, 0.95)' })
  })
});

// Adressen die in een getekend gebied vallen lichten op. Het paneel levert
// de geometrieën; elke wijziging verhoogt de revisie zodat de cache per
// punt vervalt.
const selectedAddressPointStyle = new Style({
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: '#FFD100' }),
    stroke: new Stroke({ color: '#0D2B3E', width: 1.5 })
  })
});
let addressSelectionProvider = null;
let addressSelectionRevision = 0;
let addressLayerRef = null;

export function setAddressSelectionProvider(fn) {
  addressSelectionProvider = typeof fn === 'function' ? fn : null;
  bumpAddressSelectionRevision();
}

export function bumpAddressSelectionRevision() {
  addressSelectionRevision += 1;
  addressLayerRef?.changed?.();
}

function isAddressSelected(feature) {
  if (!addressSelectionProvider) return false;
  if (feature.get('_bbSelRev') === addressSelectionRevision) return feature.get('_bbSel') === true;
  const coord = feature.getGeometry()?.getCoordinates?.();
  let inside = false;
  if (coord) {
    const geoms = addressSelectionProvider() || [];
    inside = geoms.some(g => g?.intersectsCoordinate?.(coord));
  }
  feature.set('_bbSelRev', addressSelectionRevision, true);
  feature.set('_bbSel', inside, true);
  return inside;
}

function createAddressLayerStyleFunction(styleJson) {
  const baseStyleFunction = createBaseLayerStyleFunction(styleJson);
  // Puntstijl bewust niet uit de backend-config (zie addressPointStyle).
  const pointStyle = addressPointStyle;

  return feature => {
    if (!feature) {
      return pointStyle;
    }

    if (isClosedPolygonGeometry(feature)) {
      return baseStyleFunction(feature);
    }

    return isAddressSelected(feature) ? selectedAddressPointStyle : pointStyle;
  };
}

function getDefaultStyle() {
  return new Style({
    fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' }),
    stroke: new Stroke({ color: '#0000FF', width: 1 }),
    image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#0000FF' }) })
  });
}

function getChoroplethStyle(feature) {
  const val = feature.get('u108212Aantal_VBO_BAG') || 0;

  let color = '#00FF00'; // low

  if (val > 100) color = '#FFFF00';
  if (val > 300) color = '#FFA500';
  if (val > 600) color = '#FF0000';

  return new Style({
    fill: new Fill({
      color: color + '80' // transparency
    }),
    stroke: new Stroke({
      color: '#333',
      width: 1
    })
  });
}

function generatePointsInPolygon(feature, count = 5) {
  const geom = feature.getGeometry();
  const extent = geom.getExtent();
  const points = [];

  for (let i = 0; i < count; i++) {
    const x = extent[0] + Math.random() * (extent[2] - extent[0]);
    const y = extent[1] + Math.random() * (extent[3] - extent[1]);

    const point = new GeoJSON().readFeature({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [x, y]
      },
      properties: feature.getProperties()
    }, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857'
    });

    points.push(point);
  }

  return points;
}

function getLayerRightCandidates(layerInfo) {
  const candidates = [
    layerInfo.title,
    layerInfo.typeName,
    layerInfo.typeName?.split(':').pop(),
    layerInfo.title ? `layers.base.${layerInfo.title}` : null,
    layerInfo.typeName ? `layers.base.${layerInfo.typeName}` : null,
    layerInfo.typeName ? `layers.base.${layerInfo.typeName.split(':').pop()}` : null
  ];

  return [...new Set(
    candidates
      .map(value => typeof value === 'string' ? value.trim() : '')
      .filter(Boolean)
  )];
}

function normalizeLayerRight(value) {
  if (typeof value !== 'string') return '';

  return value
    .trim()
    .toLowerCase()
    .replace(/^layers\.base\./, '')
    .replace(/^geolinq:/, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function getRightKeyVariants(value) {
  const normalized = normalizeLayerRight(value);
  if (!normalized) return [];

  const variants = new Set([normalized]);

  if (normalized.endsWith('ies')) {
    variants.add(`${normalized.slice(0, -3)}y`);
  }

  if (normalized.endsWith('es')) {
    variants.add(normalized.slice(0, -2));
  }

  if (normalized.endsWith('s')) {
    variants.add(normalized.slice(0, -1));
  }

  Object.entries(BASE_LAYER_RIGHT_ALIASES).forEach(([rightKey, aliases]) => {
    if (normalized === rightKey || aliases.includes(normalized)) {
      variants.add(rightKey);
      aliases.forEach(alias => variants.add(alias));
    }
  });

  return [...variants];
}

function getSemanticBaseLayerRole(layerInfo) {
  const candidates = getLayerRightCandidates(layerInfo)
    .map(normalizeLayerRight)
    .filter(Boolean);

  const semanticRoles = ['addresses', 'pc4', 'pc5', 'pc6'];

  for (const role of semanticRoles) {
    const aliases = BASE_LAYER_RIGHT_ALIASES[role] || [];
    const roleKey = normalizeLayerRight(role);

    if (candidates.some(candidate =>
      candidate === roleKey || aliases.some(alias => candidate === normalizeLayerRight(alias))
    )) {
      return role === 'addresses' ? 'address' : role;
    }
  }

  return null;
}

function hasBaseLayerRight(layerInfo) {
  const candidates = getLayerRightCandidates(layerInfo);
  if (candidates.some(candidate => hasRight(candidate))) return true;

  const userRightKeys = new Set(getUserRights().flatMap(getRightKeyVariants));

  return candidates.some(candidate =>
    getRightKeyVariants(candidate).some(candidateKey => userRightKeys.has(candidateKey))
  );
}

function isBaseLayerVisibleByDefault(layerInfo) {
  return hasBaseLayerRight(layerInfo) && layerInfo.visible === true;
}

function applyLayerRangeOptions(options, layerInfo) {
  const range = MapUtils.normalizeLayerVisibilityRange(layerInfo.minResolution, layerInfo.maxResolution);
  options.normalizedVisibilityRange = range;

  if (range.mode === 'zoom') {
    options.minZoom = range.minZoom;
    options.maxZoom = range.maxZoom;
  } else {
    options.minResolution = range.minResolution;
    options.maxResolution = range.maxResolution;
  }

  return range;
}


export async function loadDynamicBaseLayers(map) {
  try {

    if (!map) {
      //console.warn("loadDynamicBaseLayers skipped: base_map missing");
      return;
    }

    //get values dynamically from dropdowns
    const country = getSelectedCountryCode();
    const language = getSelectedLanguageCode();

    if (!country || !language) {
      //console.warn("Country or language not selected yet. Skipping base layer load.");
      return;
    }

    const url = `${API_URL}base-layers/raw?country=${country}&language=${language}`;

    const response = await secureFetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Server returned ${response.status}`);
    const layersData = await response.json();

    //Remove only existing base layers (don't touch overlays or own data)
    //const baseLayers = map.getLayers().getArray().filter(l => l.get('isBase'));
    //baseLayers.forEach(layer => map.getLayers().remove(layer));
    window.base_map.getLayers().clear();

    layersData.forEach(layerInfo => {
      
      if (!layerInfo.title) return;


      // Check access right
      const hasLayerRight = hasBaseLayerRight(layerInfo);
      const visibleByBackend = isBaseLayerVisibleByDefault(layerInfo);

      // Skip if user doesn't have right
      //if (!hasLayerRight) {
      //  console.log("Layer disabled by rights: ", layerInfo.title);
        //return;
      //}


      const semanticRole = getSemanticBaseLayerRole(layerInfo);
      const isAddressLayer = semanticRole === 'address';
      layerInfo = withReplacedTypeName(layerInfo);
      const propertyParam = isAddressLayer && layerInfo.typeName === BE_ADDRESS_TYPENAME
        ? `&propertyName=${BE_ADDRESS_PROPERTIES.join(',')}`
        : '';
      // Overzichtslagen (gewesten, provincies, ...) zijn al zichtbaar bij een
      // uitgezoomde kaart. Met een bbox-strategie werden ze bij het openen
      // twee keer volledig opgehaald (de view verschuift nog tijdens de init)
      // en bij elke verschuiving opnieuw. Eén keer alles laden is goedkoper.
      // Alleen echte overzichtslagen (land tot gemeenten) in één keer laden.
      // Postcode- en sectorlagen blijven per bbox: de volledige be_4pp-extract
      // komt afgekapt terug van GeoServer (ongeldige JSON na 287 kB) en weegt
      // met bbox over heel België 81 MB.
      const isOverviewLayer = OVERVIEW_TYPENAMES.has(String(layerInfo.typeName || '').toLowerCase());
      // Overzichtslagen (land, gewesten, provincies) staan in de backend-config
      // standaard aan, maar in de bewonersbrief-wizard gebruikt niemand ze en
      // de gewestenlaag alleen al is 4,3 MB. Uit bij het openen; blijven wel
      // aan te zetten in de lagenlijst. Dan laadt de kaart niets via WFS tot de
      // klant inzoomt of een laag kiest.
      const visibleByDefault = visibleByBackend && !isOverviewLayer;

      const source = new VectorSource({
        overlaps: false,
        format: new SanitizedGeoJSON({
          dataProjection: 'EPSG:3857',
          featureProjection: 'EPSG:3857'
        }),
        url: extent => {
          const bboxParam = Number.isFinite(extent[0]) ? `&bbox=${extent.join(',')},EPSG:3857` : '';
          return `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature&typename=${layerInfo.typeName}&outputFormat=application/json&srsname=EPSG:3857${bboxParam}${propertyParam}`;
        },
        // Adressen per tegel laden: kleine requests die gecachet blijven bij
        // het pannen, in plaats van telkens de hele viewport opnieuw.
        strategy: isAddressLayer
          ? tileStrategy(ADDRESS_TILE_GRID)
          : isOverviewLayer ? allStrategy : bboxStrategy
      });

      source.on('addfeature', e => {
        const feature = e.feature;
        const customId = feature.get('s_source_id');
        if (customId == null) return;

        const featureId = String(customId);
        const existingFeature = source.getFeatureById(featureId);
        if (existingFeature && existingFeature !== feature) {
          source.removeFeature(feature);
          return;
        }

        feature.setId(featureId);
      });



      let maxValue = 1;
      const heatmapSource = new VectorSource();

      source.on('addfeature', (e) => {
        if (!isHeatmapMode) return;

        const feature = e.feature;
        const geom = feature.getGeometry();

        const val = feature.get('u108212Aantal_VBO_BAG') || 0;

        // FIX: track max value
        if (val > maxValue) maxValue = val;

        if (geom.getType() === 'Polygon') {
          const extent = geom.getExtent();

          for (let i = 0; i < 15; i++) {
            const x = extent[0] + Math.random() * (extent[2] - extent[0]);
            const y = extent[1] + Math.random() * (extent[3] - extent[1]);

            const pointFeature = new GeoJSON().readFeature({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [x, y]
              },
              properties: feature.getProperties()
            }, {
              dataProjection: 'EPSG:3857',
              featureProjection: 'EPSG:3857'
            });

            heatmapSource.addFeature(pointFeature);
          }

        } else {
          heatmapSource.addFeature(feature.clone());
        }
      });

      let layer;

      if (isHeatmapMode) {

        const gradient = extractHeatmapColors(layerInfo.style);
        const heatmapOptions = {
          source: heatmapSource,
          title: layerInfo.title,
          visible: visibleByDefault,

            weight: (feature) => {
              const val = feature.get('u108212Aantal_VBO_BAG') || 0;

              if (val < 20) return 0;

              return Math.pow(val / maxValue, 0.5);
            },

          gradient: [
            'rgba(0,0,0,0)',
            'rgba(0,0,255,0.4)',
            'rgba(0,255,255,0.6)',
            'rgba(255,255,0,0.8)',
            'rgba(255,128,0,0.9)',
            'rgba(255,0,0,1)'
          ],

          blur: 30,
          radius: 40
        };

        applyLayerRangeOptions(heatmapOptions, layerInfo);

        // HEATMAP MODE
        layer = new HeatmapLayer(heatmapOptions);

        source.once('change', () => {
          if (source.getState() === 'ready') {

            // trigger heatmap refresh
            setTimeout(() => {
              layer.changed();
            }, 300);
          }
        });

            // force redraw after load
          source.on('addfeature', (e) => {
            const feature = e.feature;
            const geom = feature.getGeometry();
          });

      } else {
        const backendStyle = parseStyleFromJSON(layerInfo.style);
        const layerOptions = {
          title: layerInfo.title,
          source,
          visible: visibleByDefault,
          style: semanticRole === 'address'
            ? createAddressLayerStyleFunction(layerInfo.style)
            : createBaseLayerStyleFunction(layerInfo.style)
        };

        const range = applyLayerRangeOptions(layerOptions, layerInfo);
        if (isAddressLayer) {
          if (range.mode === 'zoom') {
            layerOptions.minZoom = Math.max(Number(layerOptions.minZoom) || -Infinity, ADDRESS_MIN_ZOOM);
          } else {
            layerOptions.maxResolution = Math.min(Number(layerOptions.maxResolution) || Infinity, ADDRESS_MAX_RESOLUTION);
          }
        }

        // NORMAL MODE
        layer = new VectorLayer(layerOptions);
      }

      if (isAddressLayer) addressLayerRef = layer;

      // common props
      layer.set('isBase', true);
      layer.set('title', layerInfo.title);
      layer.set('typeName', layerInfo.typeName);
      layer.set('backendStyle', layerInfo.style);
      layer.set('backendVisible', layerInfo.visible === true);
      layer.set('displayOrder', layerInfo.displayOrder);
      // Effectieve grenzen bewaren (voor de adreslaag kunnen die strenger zijn
      // dan de backend-config), zodat de "zoom in"-hints kloppen.
      const effectiveMax = typeof layer.getMaxResolution === 'function' ? layer.getMaxResolution() : layerInfo.maxResolution;
      const effectiveMin = typeof layer.getMinResolution === 'function' ? layer.getMinResolution() : layerInfo.minResolution;
      layer.set('configuredMinResolution', Number.isFinite(effectiveMin) && effectiveMin > 0 ? effectiveMin : layerInfo.minResolution);
      layer.set('configuredMaxResolution', Number.isFinite(effectiveMax) ? effectiveMax : layerInfo.maxResolution);
      layer.set('normalizedVisibilityRange', MapUtils.normalizeLayerVisibilityRange(
        layer.get('configuredMinResolution'),
        layer.get('configuredMaxResolution')
      ));
      layer.set('wfsSource', source);
      layer.set('disabledByRights', !hasLayerRight);
      layer.set('addressAidsRole', semanticRole);
      layer.set('semanticRole', semanticRole);

      const displayOrder = Number(layerInfo.displayOrder);
      if (semanticRole === 'address') {
        layer.setZIndex(10000);
      } else if (Number.isFinite(displayOrder)) {
        layer.setZIndex(displayOrder);
      }

      // Eén render per geladen batch, niet per feature: bij duizenden
      // adrespunten in beeld bevroor de kaart anders bij het inzoomen.
      source.on('featuresloadend', () => layer.changed());

      window.base_map.getLayers().push(layer);

      
      setTimeout(() => {
        const realMap = getMap();
        if (realMap) {
          updateBaseLegendMessage(window.base_map);
        }
      }, 0);
      
    });

    //window.base_map = map;

   //Trigger update only when layers exist
    if (map.getLayers().getLength() > 0) 
    {
      document.dispatchEvent(new CustomEvent('base-layers-updated'));
    } 
    else 
    {
      console.warn("No base layers loaded - skipping panel update");
    }

    fitBaseLayers(getMap())

  } catch (err) {
    console.error('Error loading dynamic base layers:', err);
  }
}


/*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 Fit all visible base layers on the map
 +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
export function fitBaseLayers(map) {

    if (!map || typeof map.getView !== 'function') {
    console.error('fitBaseLayers called with invalid map:', map);
    return;
  }

  const allLayers = MapUtils.getAllLayers(map); // flatten all layers
  let allExtents = [];

  allLayers.forEach(layer => {
    
    if (layer.get('isBase') && layer.getVisible()) {
      
      const source = layer.getSource?.();
      if (source) {
        const features = source.getFeatures();
        if (features.length > 0) {
          allExtents.push(source.getExtent());
        } 
      }
    }
  });

  if (allExtents.length === 0) return;

  if (allExtents.length > 0) {
    const combined = allExtents.reduce((acc, ext) => {
      return olExtent.extend(acc, ext);
    }, olExtent.createEmpty());

    map.getView().fit(combined, { duration: 500, padding: [50, 50, 50, 50] });
  } else {
    return;
  }
}

export async function fitCountry(map, countryCode) {
  try {
    if (!countryCode) {
      console.warn("fitCountry: No country code provided.");
      return;
    }


    // Replace with your GeoServer layer name and filter field
    const layerName = 'geolinq:countries';
    const cqlFilter = `code='${countryCode}'`; // or ISO_CODE / COUNTRY_ID, etc.

    const wfsUrl = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature&typename=${layerName}&outputFormat=application/json&CQL_FILTER=${encodeURIComponent(cqlFilter)}&srsname=EPSG:3857`;

    const response = await fetch(wfsUrl);
    if (!response.ok) throw new Error(`GeoServer returned ${response.status}`);

    const geojson = await response.json();
    const features = new GeoJSON().readFeatures(geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });

    if (!features || features.length === 0) {
      console.warn("fitCountry: No country geometry found.");
      return;
    }

    // Compute extent
    const extent = olExtent.createEmpty();
    features.forEach(f => olExtent.extend(extent, f.getGeometry().getExtent()));

    // Fit view
    map.getView().fit(extent, { duration: 700, padding: [50, 50, 50, 50] });

    // Optional: add temporary highlight
    const highlightLayer = new VectorLayer({
      source: new VectorSource({ features }),
      style: new Style({
        stroke: new Stroke({ color: '#FF8800', width: 3 }),
        fill: new Fill({ color: 'rgba(255,136,0,0.2)' })
      }),
    });

    // Remove previous highlight if any
    const old = map.getLayers().getArray().find(l => l.get('isCountryHighlight'));
    if (old) map.removeLayer(old);

    highlightLayer.set('isCountryHighlight', true);
    map.addLayer(highlightLayer);

    // Auto-remove highlight after 3 seconds
    setTimeout(() => map.removeLayer(highlightLayer), 3000);

  } catch (err) {
    console.error("fitCountry error:", err);
  }
}

//document.dispatchEvent(new CustomEvent('base-layers-updated'));


